import React, { useMemo } from 'react';
import { AlertTriangle, XCircle, Clock, Wifi } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const ErrorAnalysisCard = ({ errorTypes, failedCalls, rawMetrics }) => {
  const errorAnalysis = useMemo(() => {
    if (!rawMetrics || rawMetrics?.length === 0) return null;

    const failedMetrics = rawMetrics?.filter(m => !m?.success);
    
    // Categorize errors
    const categories = {
      timeout: { count: 0, icon: Clock, color: 'orange', label: 'Timeout Errors' },
      rate_limit: { count: 0, icon: AlertTriangle, color: 'yellow', label: 'Rate Limit Errors' },
      server_error: { count: 0, icon: XCircle, color: 'red', label: 'Server Errors' },
      network_error: { count: 0, icon: Wifi, color: 'blue', label: 'Network Errors' },
      other: { count: 0, icon: AlertTriangle, color: 'gray', label: 'Other Errors' }
    };

    failedMetrics?.forEach(metric => {
      const errorType = metric?.error_type || 'other';
      if (categories?.[errorType]) {
        categories[errorType].count++;
      } else {
        categories.other.count++;
      }
    });

    // Calculate success after retry rate
    const retriedCalls = rawMetrics?.filter(m => m?.retry_count > 0);
    const successAfterRetry = retriedCalls?.filter(m => m?.success)?.length;
    const retrySuccessRate = retriedCalls?.length > 0 
      ? ((successAfterRetry / retriedCalls?.length) * 100)?.toFixed(1)
      : 0;

    return {
      categories,
      retriedCalls: retriedCalls?.length,
      successAfterRetry,
      retrySuccessRate
    };
  }, [rawMetrics]);

  const getColorClasses = (color) => {
    const colors = {
      orange: 'bg-orange-100 text-orange-800 border-orange-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      red: 'bg-red-100 text-red-800 border-red-200',
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      gray: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors?.[color] || colors?.gray;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
          <AlertTriangle className="w-5 h-5 text-red-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Error Analysis Dashboard</h3>
          <p className="text-sm text-gray-500">Failure categorization and retry effectiveness</p>
        </div>
      </div>
      {errorAnalysis ? (
        <>
          {/* Error Categories */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {Object.entries(errorAnalysis?.categories)?.filter(([_, data]) => data?.count > 0)?.map(([type, data]) => {
                const Icon = data?.icon;
                const percentage = ((data?.count / failedCalls) * 100)?.toFixed(1);

                return (
                  <div key={type} className={`border rounded-lg p-4 ${getColorClasses(data?.color)}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <Icon className="w-5 h-5" />
                      <span className="font-semibold">{data?.label}</span>
                    </div>
                    <p className="text-2xl font-bold">{data?.count}</p>
                    <p className="text-sm mt-1">{percentage}% of failures</p>
                  </div>
                );
              })}
          </div>

          {/* Retry Effectiveness */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border border-purple-200">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Retry Effectiveness</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Retried Calls</p>
                <p className="text-3xl font-bold text-purple-900">{errorAnalysis?.retriedCalls}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Successful After Retry</p>
                <p className="text-3xl font-bold text-green-900">{errorAnalysis?.successAfterRetry}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Retry Success Rate</p>
                <p className="text-3xl font-bold text-blue-900">{errorAnalysis?.retrySuccessRate}%</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-purple-200">
              <p className="text-sm text-gray-700">
                <strong>Insight:</strong> {errorAnalysis?.retrySuccessRate >= 50 
                  ? `Retry mechanism is effective - ${errorAnalysis?.retrySuccessRate}% of retried calls eventually succeed.`
                  : `Retry mechanism needs optimization - only ${errorAnalysis?.retrySuccessRate}% of retried calls succeed.`
                }
              </p>
            </div>
          </div>

          {/* Recommendations */}
          {failedCalls > 0 && (
            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Recommendations
              </h4>
              <ul className="space-y-1 text-sm text-yellow-800">
                {errorAnalysis?.categories?.timeout?.count > 0 && (
                  <li>• Consider increasing timeout duration or optimizing request payload size</li>
                )}
                {errorAnalysis?.categories?.rate_limit?.count > 0 && (
                  <li>• Implement request queuing or upgrade API tier to handle higher rate limits</li>
                )}
                {errorAnalysis?.categories?.server_error?.count > 0 && (
                  <li>• Monitor provider status pages and implement fallback mechanisms</li>
                )}
                {errorAnalysis?.categories?.network_error?.count > 0 && (
                  <li>• Check network connectivity and DNS resolution for API endpoints</li>
                )}
              </ul>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <AlertTriangle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>No error data available</p>
        </div>
      )}
    </div>
  );
};

export default ErrorAnalysisCard;